<template>
  <Toolbar class="p-mb-4 p-mt-4 p-p-2">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Documents') }}</div>
    </template>
    <template #end>
      <div class="p-p-1">
        <Button @click="saveSettings"
                :disabled="!$store.getters.isAccountantOrHigher || locationIsLoading || fetchInFlight"
                :loading="locationIsLoading || fetchInFlight"
                class="p-button-raised"
                icon="ti-save"
                :label="$t('Save')"/>
      </div>
    </template>
  </Toolbar>

  <div class="p-grid">
    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-2">
      <fieldset class="p-fieldset p-pb-4">
        <legend class="p-fieldset-legend p-p-2">{{ $t('General settings') }}</legend>
        <div class="p-mt-2 medium-bold-text">{{ $t('documentPrefixesTitle') }}</div>
        <div class="p-mt-5">
          <div class="p-float-label">
            <InputText id="order-prefix"
                       class="settings-input"
                       :class="{'p-invalid' : submitted && !orderPrefix}"
                       v-model.trim="orderPrefix"
                       autocomplete="new-password"/>
            <label for="order-prefix">{{ $t('orderPrefixLabel') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !orderPrefix">{{ $t('Required field') }}</small>
        </div>

        <div class="p-mt-5">
          <div class="p-float-label">
            <InputText id="estimate-prefix"
                       class="settings-input"
                       :class="{'p-invalid' : submitted && !estimatePrefix}"
                       v-model.trim="estimatePrefix"
                       autocomplete="new-password"/>
            <label for="estimate-prefix">{{ $t('estimatePrefixLabel') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !estimatePrefix">{{ $t('Required field') }}</small>
        </div>


        <div class="p-mt-5">
          <div class="p-float-label">
            <InputText id="sales-invoice-prefix"
                       class="settings-input"
                       :class="{'p-invalid' : submitted && !salesInvoicePrefix}"
                       v-model.trim="salesInvoicePrefix"
                       autocomplete="new-password"/>
            <label for="sales-invoice-prefix">{{ $t('salesInvoicePrefixLabel') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !salesInvoicePrefix">{{ $t('Required field') }}</small>
        </div>

        <div class="p-mt-5">
          <div class="p-float-label">
            <InputText id="prepayment-invoice-prefix"
                       class="settings-input"
                       :class="{'p-invalid' : submitted && !prepaymentInvoicePrefix}"
                       v-model.trim="prepaymentInvoicePrefix"
                       autocomplete="new-password"/>
            <label for="prepayment-invoice-prefix">{{ $t('prepaymentInvoicePrefixLabel') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !prepaymentInvoicePrefix">{{ $t('Required field') }}</small>
        </div>

        <div class="p-mt-5">
          <div class="p-float-label">
            <InputText id="work-order-prefix"
                       class="settings-input"
                       :class="{'p-invalid' : submitted && !workOrderPrefix}"
                       v-model.trim="workOrderPrefix"
                       autocomplete="new-password"/>
            <label for="work-order-prefix">{{ $t('workOrderPrefixLabel') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !workOrderPrefix">{{ $t('Required field') }}</small>
        </div>

        <hr class="p-mt-4 p-mb-4">

        <div class="medium-bold-text">{{ $t('documentPrimaryColor') }}</div>

        <div class="document-color-row p-mt-3">
          <ColorPicker :key="documentPrimaryColorPickerKey"
                       v-model="documentPrimaryColor"
                       format="hex"
                       @update:modelValue="onDocumentPrimaryColorPickerUpdate"/>

          <InputText v-model="documentPrimaryColorText"
                     class="document-color-input"
                     maxlength="7"
                     placeholder="#b5b5b5"
                     @input="onDocumentPrimaryColorTextInput"
                     @blur="normalizeDocumentPrimaryColorText"/>
        </div>

        <hr class="p-mt-4 p-mb-4">

        <div class="medium-bold-text">{{ $t('defaultSenderEmailsTitle') }}</div>
        <div v-if="emailAccountsLoading" class="closed-state p-mt-5">
          <Spinner :isSmall="true"/>
        </div>
        <NoticePlaceholder v-else-if="!emailAccounts?.length"
                           icon="ti-email"
                           :title="$t('noEmailAccountsAvailableTitle')"
                           :text="$t('noEmailAccountsAvailableText')"/>

        <template v-else>
          <div class="p-mt-5">
            <div class="row-with-help">
              <div class="p-float-label field">
                <CustomDropdown id="sales-invoices-email-account"
                                class="settings-input"
                                v-model="selectedSalesInvoicesEmailAccount"
                                :options="emailAccounts"
                                data-key="id"
                                :filter="true"
                                :filterFields="['username']"
                                :showClear="true">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ slotProps.value.username }}</span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div>
                      <span>{{ slotProps.option.username }}</span>
                    </div>
                  </template>
                </CustomDropdown>
                <label for="sales-invoices-email-account">
                  {{ $t('invoicesEmailAccountLabel') }}
                </label>
              </div>
              <i v-tooltip="$t('invoicesEmailAccountTooltip')" class="ti-help-alt cursor-help p-ml-1"/>
            </div>
          </div>

          <div class="p-mt-5">
            <div class="row-with-help">
              <div class="p-float-label field">
                <CustomDropdown id="estimates-email-account"
                                class="settings-input"
                                v-model="selectedEstimatesEmailAccount"
                                :options="emailAccounts"
                                data-key="id"
                                :filter="true"
                                :filterFields="['username']"
                                :showClear="true">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ slotProps.value.username }}</span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div>
                      <span>{{ slotProps.option.username }}</span>
                    </div>
                  </template>
                </CustomDropdown>
                <label for="estimates-email-account">
                  {{ $t('estimatesEmailAccountLabel') }}
                </label>
              </div>
              <i v-tooltip="$t('estimatesEmailAccountTooltip')" class="ti-help-alt cursor-help p-ml-1"/>
            </div>
          </div>

        </template>
      </fieldset>
    </div>

    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-2">
      <fieldset class="p-fieldset p-pb-4">
        <legend class="p-fieldset-legend p-p-2">{{ $t('documentAdditionalTextsTitle') }}</legend>

        <div class="p-mt-2 medium-bold-text">{{ $t('Estimate') }}:</div>
        <div class="p-mt-2">
          <ModifiedEditor id="settings-estimate-document-notes"
                          v-model="estimateAdditionalInfo"
                          @text-change="changeEstimateAdditionalInfo"
                          editorStyle="height: 140px"/>
          <small v-if="estimateError" class="p-invalid">{{ estimateError }}</small>
        </div>

<!--        <hr class="p-mt-4 p-mb-3">-->

        <div class="p-mt-4 medium-bold-text">{{ $t('Sales invoice') }}:</div>
        <div class="p-mt-2">
          <ModifiedEditor id="settings-sales-invoice-document-notes"
                          v-model="salesInvoiceAdditionalInfo"
                          @text-change="changeSalesInvoiceAdditionalInfo"
                          editorStyle="height: 140px"/>
          <small v-if="salesInvoiceError" class="p-invalid">{{ salesInvoiceError }}</small>
        </div>

<!--        <hr class="p-mt-4 p-mb-3">-->

        <div class="p-mt-4 medium-bold-text">{{ $t('Prepayment Invoice') }}:</div>
        <div class="p-mt-2">
          <ModifiedEditor id="settings-prepayment-invoice-document-notes"
                          v-model="prepaymentInvoiceAdditionalInfo"
                          @text-change="changePrepaymentInvoiceAdditionalInfo"
                          editorStyle="height: 140px"/>
          <small v-if="prepaymentInvoiceError" class="p-invalid">{{ prepaymentInvoiceError }}</small>
        </div>


      </fieldset>
    </div>


    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4">
      <fieldset class="p-fieldset p-pb-4">
        <legend class="p-fieldset-legend p-p-2">{{ $t('bankAccountsTitle') }}</legend>
        <div class="bank-accounts-block p-mt-2">

          <div class="p-mt-2 p-mb-3 p-d-flex p-ai-center p-jc-start p-flex-wrap">
            <Button :label="$t('Add')"
                    icon="pi pi-plus"
                    class="p-button-sm p-button-raised p-mr-1"
                    :disabled="bankAccounts.length >= 3"
                    @click="addBankAccount"/>

            <div class="p-text-secondary p-p-2">
              {{ bankAccounts.length }}/3
            </div>
          </div>

          <NoticePlaceholder v-if="bankAccounts.length === 0"
                             :title="$t('noBankAccountsTitle')"
                             :text="$t('noBankAccountsText')"/>

          <div v-for="(acc, idx) in bankAccounts" :key="idx">
            <div class="p-mb-2" style="display:flex; align-items:center; justify-content:space-between;">
              <div class="medium-bold-text">
                {{ idx === 0 ? $t('mainBankAccountLabel') : $t('bankAccountNumberLabel', { number: idx + 1 }) }}
              </div>

              <Button icon="ti-trash"
                      class="p-button-text p-button-danger p-button-sm"
                      @click="removeBankAccount(idx)"/>
            </div>

            <div class="p-mt-4">
              <div class="p-float-label">
                <InputText :id="`bank-name-${idx}`"
                           class="settings-input"
                           :class="{ 'p-invalid': submitted && (!acc.name || acc.name === '') }"
                           v-model.trim="acc.name"
                           autocomplete="off"/>
                <label :for="`bank-name-${idx}`">
                  {{ $t('bankAccountNameLabel') }}<span class="warning-color">*</span>
                </label>
              </div>
              <small class="p-invalid" v-if="submitted && (!acc.name || acc.name === '')">{{ $t('Required field') }}</small>
            </div>

            <!-- IBAN -->
            <div class="p-mt-5">
              <div class="p-float-label">
                <InputText :id="`bank-iban-${idx}`"
                           class="settings-input"
                           :class="{ 'p-invalid': submitted && !isIbanValid(acc.iban) }"
                           v-model="acc.iban"
                           autocomplete="off"
                           @blur="acc.iban = normalizeIban(acc.iban)"/>
                <label :for="`bank-iban-${idx}`">IBAN<span class="warning-color">*</span></label>
              </div>
              <small class="p-invalid" v-if="submitted && ibanError(acc.iban)">{{ ibanError(acc.iban) }}</small>
            </div>

            <!-- BIC -->
            <div class="p-mt-5">
              <div class="p-float-label">
                <InputText :id="`bank-bic-${idx}`"
                           class="settings-input"
                           :class="{
                            'p-invalid': submitted && acc.bic && !isBicValid(acc.bic)
                           }"
                           v-model="acc.bic"
                           autocomplete="off"
                           @blur="acc.bic = normalizeBic(acc.bic)"/>
                <label :for="`bank-bic-${idx}`">{{ $t('bicSwiftOptionalLabel') }}</label>
              </div>
              <small class="p-invalid" v-if="submitted && bicError(acc.bic)">{{ bicError(acc.bic) }}</small>
            </div>

            <hr class="p-mt-4 p-mb-4" v-if="idx < bankAccounts.length - 1"/>
          </div>
        </div>
      </fieldset>
    </div>

  </div>
</template>

<script>
import showErrorsMixins from "@/mixins/showErrorsMixins";
import formatMixins from "@/mixins/formatMixins";
import httpClient from "@/services/http.services";
import settings from "@/settings";
import NoticePlaceholder from "@/components/NoticePlaceholder";
import ModifiedEditor from "@/components/ModifiedEditor";

const MAX_LEN = 1200
const DEFAULT_DOCUMENT_PRIMARY_COLOR = '#b5b5b5'

export default {
  emits: ['update-items'],
  components: { NoticePlaceholder, ModifiedEditor },
  mixins: [ showErrorsMixins, formatMixins ],
  props: {
    locationData: {
      type: Object,
      default: null,
    },
    locationIsLoading: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      documentPrimaryColorPickerKey: 0,
      bankAccounts: [],
      estimateAdditionalInfo: null,
      salesInvoiceAdditionalInfo: null,
      prepaymentInvoiceAdditionalInfo: null,
      emailAccounts: [],
      emailAccountsLoading: false,
      emailAccountsLoaded: false,
      selectedEstimatesEmailAccount: null,
      selectedSalesInvoicesEmailAccount: null,
      fetchInFlight: false,
      submitted: false,
      isLoading: true,
      orderPrefix: null,
      estimatePrefix: null,
      salesInvoicePrefix: null,
      prepaymentInvoicePrefix: null,
      workOrderPrefix: null,
      documentPrimaryColor: DEFAULT_DOCUMENT_PRIMARY_COLOR.replace(/^#/, ''),
      documentPrimaryColorText: '',
      estimateError: null,
      salesInvoiceError: null,
      prepaymentInvoiceError: null,
    }
  },
  mounted() {
    this.applySettingsBase();
    this.loadEmailAccounts();
  },
  methods: {
    async saveSettings() {
      this.submitted = true

      const locationId = this.locationData?.id

      const orderPrefix = (this.orderPrefix ?? '').trim()
      const estimatePrefix = (this.estimatePrefix ?? '').trim()
      const salesInvoicePrefix = (this.salesInvoicePrefix ?? '').trim()
      const prepaymentInvoicePrefix = (this.prepaymentInvoicePrefix ?? '').trim()
      const workOrderPrefix = (this.workOrderPrefix ?? '').trim()
      this.normalizeDocumentPrimaryColorText()

      const documentPrimaryColor = this.normalizeDocumentPrimaryColorForServer(
          this.documentPrimaryColorText
      )

      if (!locationId || this.fetchInFlight) return

      if (!orderPrefix || !estimatePrefix || !salesInvoicePrefix || !prepaymentInvoicePrefix || !workOrderPrefix) {
        return false
      }

      if (this.estimateError || this.salesInvoiceError || this.prepaymentInvoiceError) {
        return false
      }

      let wrongInputs = false
      this.bankAccounts?.forEach(acc => {
        if (!this.isIbanValid(acc.iban) ||
            !this.isBicValid(acc.bic) ||
            !acc.name || acc.name === ''
        ) wrongInputs = true
      })

      if (wrongInputs) return

      this.fetchInFlight = true

      const payload = {
        location_id: locationId,
        order_prefix: orderPrefix,
        estimate_prefix: estimatePrefix,
        sales_invoice_prefix: salesInvoicePrefix,
        prepayment_invoice_prefix: prepaymentInvoicePrefix,
        work_order_prefix: workOrderPrefix,
        document_primary_color: documentPrimaryColor,
        ...(this.emailAccountsLoaded ? {
          invoices_email_account_id: this.selectedSalesInvoicesEmailAccount?.id ?? null,
          estimates_email_account_id: this.selectedEstimatesEmailAccount?.id ?? null,
        } : {}),
        estimate_additional_info: this.estimateAdditionalInfo ?? null,
        sales_invoice_additional_info: this.salesInvoiceAdditionalInfo ?? null,
        prepayment_invoice_additional_info: this.prepaymentInvoiceAdditionalInfo ?? null,
        bank_accounts: this.bankAccounts ?? null,
      }

      try {
        const { data, status } = await httpClient.post('location/settings-update', payload)
        if (status === 200 && data?.success) {
          this.$emit('update-items', locationId)
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife })

        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.fetchInFlight = false
        this.submitted = false
      }
    },
    ibanError(v) {
      const iban = this.normalizeIban(v);

      // required
      if (!iban) return this.$t('Required field');

      // IBAN length is 15..34 by standard (country-specific fixed length inside this range)
      if (iban.length < 15 || iban.length > 34) return this.$t('Invalid format');

      // allowed chars only
      if (!/^[A-Z0-9]+$/.test(iban)) return this.$t('Invalid format');

      // optional: basic prefix check (uncomment if you want slightly stricter)
      // if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return 'Invalid IBAN format';

      return null;
    },

    bicError(v) {
      const bic = this.normalizeBic(v);

      // optional
      if (!bic) return null;

      // allowed chars only
      if (!/^[A-Z0-9]+$/.test(bic)) return this.$t('Invalid format');

      // must be 8 or 11
      if (!(bic.length === 8 || bic.length === 11)) return this.$t('Invalid format');

      return null;
    },

    addBankAccount() {
      if (this.bankAccounts.length >= 3) return;
      this.bankAccounts.push({ name: '', iban: '', bic: '' });
    },

    removeBankAccount(idx) {
      this.bankAccounts.splice(idx, 1);
    },

    normalizeIban(v) {
      return (v ?? '').toString().trim().replace(/\s+/g, '').toUpperCase();
    },

    normalizeBic(v) {
      return (v ?? '').toString().trim().replace(/\s+/g, '').toUpperCase();
    },

    isIbanValid(v) {
      return !this.ibanError(v);
    },

    isBicValid(v) {
      return !this.bicError(v);
    },

    changeEstimateAdditionalInfo(data) {
      const textLength = data.textValue.length;

      if (textLength > MAX_LEN) {
        this.estimateError = this.$t('maxCharsAllowedMsg', { max: MAX_LEN })
        return;
      }

      this.estimateError = null;
    },
    changeSalesInvoiceAdditionalInfo(data) {
      const textLength = data.textValue.length;

      if (textLength > MAX_LEN) {
        this.salesInvoiceError = this.$t('maxCharsAllowedMsg', { max: MAX_LEN })
        return;
      }

      this.salesInvoiceError = null;
    },
    changePrepaymentInvoiceAdditionalInfo(data) {
      const textLength = data.textValue.length;

      if (textLength > MAX_LEN) {
        this.prepaymentInvoiceError = this.$t('maxCharsAllowedMsg', { max: MAX_LEN })
        return;
      }

      this.prepaymentInvoiceError = null;
    },
    applySettingsBase() {
      const s = this.locationData || {};
      if (!s) return;

      this.orderPrefix = s.order_prefix
      this.estimatePrefix = s.estimate_prefix
      this.salesInvoicePrefix = s.sales_invoice_prefix
      this.prepaymentInvoicePrefix = s.prepayment_invoice_prefix
      this.workOrderPrefix = s.work_order_prefix

      const documentPrimaryColor = this.normalizeDocumentPrimaryColorForServer(
          s.document_primary_color
      )

      if (documentPrimaryColor) {
        this.documentPrimaryColor = documentPrimaryColor.replace(/^#/, '')
        this.documentPrimaryColorText = documentPrimaryColor
      } else {
        this.documentPrimaryColor = DEFAULT_DOCUMENT_PRIMARY_COLOR.replace(/^#/, '')
        this.documentPrimaryColorText = ''
      }

      this.estimateAdditionalInfo = s.estimate_additional_info
      this.salesInvoiceAdditionalInfo = s.sales_invoice_additional_info
      this.prepaymentInvoiceAdditionalInfo = s.prepayment_invoice_additional_info

      if (s.bank_accounts) {
        try { this.bankAccounts = JSON.parse(s.bank_accounts); }
        catch { this.bankAccounts = []; }
      } else {
        this.bankAccounts = [];
      }
    },
    applyEmailDefaultsSelection() {
      const s = this.locationData || {};
      if (!s) return;

      const estId = s.estimates_email_account_id ? +s.estimates_email_account_id : null
      const invId = s.invoices_email_account_id ? +s.invoices_email_account_id : null

      if (!Array.isArray(this.emailAccounts) || !this.emailAccounts.length) {
        this.selectedEstimatesEmailAccount = null
        this.selectedSalesInvoicesEmailAccount = null
        return
      }

      this.selectedEstimatesEmailAccount =
          estId ? (this.emailAccounts.find(a => +a.id === estId) || null) : null

      this.selectedSalesInvoicesEmailAccount =
          invId ? (this.emailAccounts.find(a => +a.id === invId) || null) : null
    },

    async loadEmailAccounts() {
      if (!this.locationData?.id || this.emailAccountsLoading) return;

      this.emailAccountsLoading = true;
      try {
        const { data, status } = await httpClient.get('location/document-email-accounts', {
          params: { location_id: this.locationData.id },
        })
        this.emailAccountsLoaded = status === 200 && !!data?.success;
        this.emailAccounts = this.emailAccountsLoaded ? (data.items || []) : [];

        // only selection depends on async list
        this.applyEmailDefaultsSelection();
      } catch (e) {
        this.emailAccountsLoaded = false;
        this.showError(e);
        this.emailAccounts = [];
        this.applyEmailDefaultsSelection();
      } finally {
        this.emailAccountsLoading = false;
      }
    },
    onDocumentPrimaryColorPickerUpdate(v) {
      const hex = this.normalizeDocumentPrimaryColorForPicker(v)

      if (!hex) {
        this.documentPrimaryColor = DEFAULT_DOCUMENT_PRIMARY_COLOR.replace(/^#/, '')
        this.documentPrimaryColorText = ''
        return
      }

      this.documentPrimaryColor = hex
      this.documentPrimaryColorText = `#${hex}`
    },

    // onDocumentPrimaryColorTextInput() {
    //   const s = (this.documentPrimaryColorText ?? '').toString().trim()
    //   const hex = s.replace(/^#/, '')
    //
    //   if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
    //     this.documentPrimaryColor = hex.toLowerCase()
    //     this.documentPrimaryColorText = `#${hex.toLowerCase()}`
    //   }
    // },
    onDocumentPrimaryColorTextInput() {
      const s = (this.documentPrimaryColorText ?? '').toString().trim()

      if (!s) {
        this.documentPrimaryColor =
            DEFAULT_DOCUMENT_PRIMARY_COLOR.replace(/^#/, '')
        this.documentPrimaryColorText = ''
        this.documentPrimaryColorPickerKey++
        return
      }

      const hex = s.replace(/^#/, '')

      if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
        this.documentPrimaryColor = hex.toLowerCase()
        this.documentPrimaryColorText = `#${hex.toLowerCase()}`
      }
    },

    normalizeDocumentPrimaryColorText() {
      const textColor = (this.documentPrimaryColorText ?? '').toString().trim()

      if (!textColor) {
        this.documentPrimaryColor = DEFAULT_DOCUMENT_PRIMARY_COLOR.replace(/^#/, '')
        this.documentPrimaryColorText = ''
        return
      }

      const serverColor = this.normalizeDocumentPrimaryColorForServer(textColor)


      if (serverColor) {
        const hex = serverColor.replace(/^#/, '')

        this.documentPrimaryColor = hex
        this.documentPrimaryColorText = serverColor

        return
      }

      const hex = this.normalizeDocumentPrimaryColorForPicker(this.documentPrimaryColor)

      if (hex) {
        this.documentPrimaryColor = hex
        this.documentPrimaryColorText = `#${hex}`
      } else {
        this.documentPrimaryColor = null
        this.documentPrimaryColorText = ''
      }
    },

    normalizeDocumentPrimaryColorForPicker(v) {
      const s = (v ?? '').toString().trim()

      if (!s) {
        return null
      }

      const hex = s.replace(/^#/, '')

      return /^[0-9A-Fa-f]{6}$/.test(hex)
          ? hex.toLowerCase()
          : null
    },

    normalizeDocumentPrimaryColorForServer(v) {
      const s = (v ?? '').toString().trim()

      if (!s) {
        return null
      }

      const hex = s.replace(/^#/, '')

      return /^[0-9A-Fa-f]{6}$/.test(hex)
          ? `#${hex.toLowerCase()}`
          : null
    },
  },
}
</script>

<style scoped lang="scss">
.row-with-help {
  display: flex;
  align-items: center;
  gap: 8px;
}

.row-with-help .field {
  flex: 0 1 280px;
  min-width: 0;
}

.row-with-help .help {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  cursor: help;
  position: relative;
  z-index: 5;
}

.settings-input {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber) {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
}

.settings-input :deep(.p-inputnumber-input),
.settings-input :deep(input.p-inputtext) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber-button-group) {
  flex: 0 0 auto;
}

.p-col-12,
.p-sm-12,
.p-md-4,
.p-lg-4,
.p-fieldset {
  min-width: 0;
}


.document-color-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.document-color-input {
  width: 120px;
  font-family: monospace;
}
</style>
